/**
 * User Domain Model
 * Represents a user in the system with role-based access control
 */

export class User {
  constructor(data) {
    const firstName = String(data.firstName || '').trim();
    const lastName = String(data.lastName || '').trim();
    const combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const fallbackName = String(data.name || '').trim();

    this.id = data.id;
    this.email = data.email;
    this.firstName = firstName;
    this.lastName = lastName;
    this.name = combinedName || fallbackName || String(data.email || '').trim();
    this.role = data.role || 'student'; // admin, course_manager, student
    this.organizationName = String(data.organizationName || '').trim() || null;
    this.enrolledCourses = data.enrolledCourses || []; // Courses user is enrolled in (for students)
    this.suspendedCourses = data.suspendedCourses || []; // Course-level student suspensions
    this.managedCourses = data.managedCourses || []; // Courses user manages (for course managers)
    this.createdByUserId = data.createdByUserId || null; // User who created this account (if tracked)
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
    this.lastLoginAt = data.lastLoginAt || null;
    this.lastActivityAt = data.lastActivityAt || null;
    this.avatar = data.avatar || null;
    this.bio = data.bio || '';
    this.passwordHash = data.passwordHash || null; // For authentication
    this.googleId = data.googleId || null; // Google OAuth ID
  }

  /**
   * Check if user is enrolled in a course
   */
  isEnrolledIn(courseId) {
    return this.enrolledCourses.includes(courseId);
  }

  /**
   * Enroll user in a course
   */
  enroll(courseId) {
    if (!this.isEnrolledIn(courseId)) {
      this.enrolledCourses.push(courseId);
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * Unenroll user from a course
   */
  unenroll(courseId) {
    const index = this.enrolledCourses.indexOf(courseId);
    if (index > -1) {
      this.enrolledCourses.splice(index, 1);
      this.suspendedCourses = this.suspendedCourses.filter((id) => id !== courseId);
      this.updatedAt = new Date().toISOString();
    }
  }

  isSuspendedIn(courseId) {
    return this.suspendedCourses.includes(courseId);
  }

  suspendInCourse(courseId) {
    if (!this.isSuspendedIn(courseId)) {
      this.suspendedCourses.push(courseId);
      this.updatedAt = new Date().toISOString();
    }
  }

  unsuspendInCourse(courseId) {
    const index = this.suspendedCourses.indexOf(courseId);
    if (index > -1) {
      this.suspendedCourses.splice(index, 1);
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * Check if user has admin privileges
   */
  isAdmin() {
    return this.role === 'admin';
  }

  /**
   * Check if user has course manager privileges
   */
  isCourseManager() {
    return this.role === 'course_manager' || this.role === 'admin';
  }

  /**
   * Update user profile
   */
  updateProfile(updates) {
    const allowedFields = ['name', 'firstName', 'lastName', 'email', 'bio', 'avatar'];
    allowedFields.forEach(field => {
      if (updates[field] !== undefined) {
        this[field] = updates[field];
      }
    });

    const normalizedFirstName = String(this.firstName || '').trim();
    const normalizedLastName = String(this.lastName || '').trim();
    const recomposedName = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ').trim();
    if (recomposedName) {
      this.name = recomposedName;
    }

    this.updatedAt = new Date().toISOString();
  }

  /**
   * Update last login time
   */
  updateLastLogin() {
    this.lastLoginAt = new Date().toISOString();
  }

  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      id: this.id,
      email: this.email,
      name: this.name,
      firstName: this.firstName,
      lastName: this.lastName,
      role: this.role,
      organizationName: this.organizationName,
      enrolledCourses: this.enrolledCourses,
      suspendedCourses: this.suspendedCourses,
      managedCourses: this.managedCourses,
      createdByUserId: this.createdByUserId,
      isActive: this.isActive,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      lastLoginAt: this.lastLoginAt,
      lastActivityAt: this.lastActivityAt,
      avatar: this.avatar,
      bio: this.bio,
      passwordHash: this.passwordHash,
      googleId: this.googleId,
    };
  }

  /**
   * Create from JSON
   */
  static fromJSON(json) {
    return new User(json);
  }
}
